import { useCallback, useContext, useMemo } from 'react'
import { UserDocument } from '@/features/user/your-practice/documents/lib/userDocuments.slice'
import { AdminYearEndReviewContext } from './context'
import {
  Document,
  EndOfYearAdminReviewStep,
  EndOfYearReviewStepStatus,
  IDocumentsStepContext,
  Receipt,
} from '../types'
import { sortDocuments } from './helpers'

const { pending, userActionRequired } = EndOfYearReviewStepStatus

export const useAdminDocuments = <T extends Document | Receipt>({
  step,
  stepContextDocuments = [],
  otherDocumentIds = [],
  userDocuments = [],
}: {
  step: EndOfYearAdminReviewStep
  stepContextDocuments?: T[]
  otherDocumentIds?: number[]
  userDocuments: UserDocument[]
}) => {
  const { updateStep } = useContext(AdminYearEndReviewContext)

  const getFileIfExists = useCallback(
    (document: T) => {
      const userDocument = userDocuments.find(
        (ud) => ud.id === document.documentId
      )
      if (userDocument?.signedUrl && userDocument?.fileDescription) {
        return {
          name: userDocument.fileDescription,
          href: userDocument.signedUrl,
        }
      }
      return undefined
    },
    [userDocuments]
  )

  const updateStepUsing = useCallback(
    (context: IDocumentsStepContext) => {
      const stepStatus = context.documents?.some((doc) => doc.requestReUpload)
        ? userActionRequired
        : pending
      return updateStep({
        step,
        status: stepStatus,
        context: { ...context },
      })
    },
    [updateStep, step]
  )

  // Filters context documents to show:
  // 1. Documents with a matching file in userDocuments
  // 2. Documents marked as "notRelevant"
  // 3. Documents with updatedByUser flag (so admin sees "Reuploaded by user" indicator)
  const filteredDocuments = useMemo(
    () =>
      stepContextDocuments.filter((doc) => {
        const existsById =
          doc.documentId && userDocuments.some((ud) => ud.id === doc.documentId)
        const hasUpdatedByUser = doc.updatedByUser === true
        return 'notRelevant' in doc
          ? doc.notRelevant || existsById || hasUpdatedByUser
          : existsById || hasUpdatedByUser
      }),
    [stepContextDocuments, userDocuments]
  )

  const hasDocuments = useMemo(
    () => filteredDocuments.length || otherDocumentIds?.length,
    [filteredDocuments, otherDocumentIds]
  )

  const updatedByUser = useMemo(
    () => filteredDocuments.some((r) => r.updatedByUser),
    [filteredDocuments]
  )

  const sortedDocuments = useMemo(
    () => sortDocuments(filteredDocuments),
    [filteredDocuments]
  )

  return {
    getFileIfExists,
    updateStepUsing,
    sortedDocuments,
    hasDocuments,
    updatedByUser,
  }
}
